import type { AcpSessionChangeData } from '@crowdex/agent-contracts';
import type { AcpSelectConfigOption } from '@/lib/desktop-config-options';
import { flattenConfigOptions, MODEL_LIST_RETRY } from '@/lib/desktop-config-options';
import { PREVIEW_EFFORT_OPTION, PREVIEW_MODEL_OPTION } from '@/lib/desktop-preview-data';
import type { ConversationScope } from '@/lib/desktop-shell/conversation-scope';
import { withSessionEvent } from '@/lib/session-events';

/** The connection surface the turn controls drive (settings and model). */
type TurnConnection = {
  planFirst: { enabled: boolean; set: (enabled: boolean) => void } | null;
  setConfigOption: (configId: string, value: string) => Promise<unknown>;
};

export type ConversationTurnControls = {
  recordSessionChanges: (changes: readonly AcpSessionChangeData[]) => void;
  changePlanFirst: (enabled: boolean) => void;
  changeModelConfig: (configId: string, value: string) => Promise<void>;
};

/**
 * The handlers that drive the active conversation turn: Plan first,
 * the model, effort or permissions (each recorded as a transcript session event
 * in the conversation they were changed in, even once another is shown).
 */
export function createConversationTurnControls(deps: {
  preview: boolean;
  busy: boolean;
  connection: TurnConnection;
  modelConfigOptions: readonly AcpSelectConfigOption[];
  /** The conversation whose settings these are. */
  conversation: Pick<ConversationScope, 'setMessages'>;
  setPreviewModel: (value: string) => void;
  setPreviewEffort: (value: string) => void;
  setPreferredModelId: (value: string | null) => void;
}): ConversationTurnControls {
  function recordSessionChanges(changes: readonly AcpSessionChangeData[]): void {
    deps.conversation.setMessages((current) => withSessionEvent(current, changes));
  }

  function changePlanFirst(enabled: boolean): void {
    const planFirst = deps.connection.planFirst;
    if (planFirst === null || planFirst.enabled === enabled) return;
    planFirst.set(enabled);
    recordSessionChanges([
      { field: 'plan', from: enabled ? 'Off' : 'On', to: enabled ? 'On' : 'Off' },
    ]);
  }

  async function changeModelConfig(configId: string, value: string): Promise<void> {
    const option = deps.modelConfigOptions.find((candidate) => candidate.id === configId);
    if (option === undefined || option.currentValue === value) return;
    // Retry asks the agent for its models again: nothing changed, nothing to record.
    if (value === MODEL_LIST_RETRY) {
      if (!deps.preview) await deps.connection.setConfigOption(configId, value);
      return;
    }
    const choices = flattenConfigOptions(option);
    const previous = choices.find((choice) => choice.value === option.currentValue)?.name;
    const next = choices.find((choice) => choice.value === value)?.name ?? value;
    if (deps.preview) {
      if (configId === PREVIEW_MODEL_OPTION.id) deps.setPreviewModel(value);
      if (configId === PREVIEW_EFFORT_OPTION.id) deps.setPreviewEffort(value);
    } else {
      await deps.connection.setConfigOption(configId, value);
      if (option.category === 'model') {
        deps.setPreferredModelId(value || null);
      }
    }
    const changes = [
      {
        field:
          option.category === 'thought_level'
            ? ('effort' as const)
            : option.category === 'permission'
              ? ('permissions' as const)
              : ('model' as const),
        from: previous ?? option.currentValue,
        to: next,
      },
    ];
    deps.conversation.setMessages((current) =>
      withSessionEvent(
        current,
        changes,
        option.category === 'model' && value ? { modelId: value } : undefined,
      ),
    );
  }

  return {
    recordSessionChanges,
    changePlanFirst,
    changeModelConfig,
  };
}
